import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';

import { takeUntil } from 'rxjs';

import { CoreService, DialogService, FormNavigationService, SpinnerService } from 'dspx-core';

import { API_ENDPOINT_KEYS, API_ENDPOINTS } from '../../constants/api-endpoints.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { TEMPLATES } from '../../constants/templates.constants';
import {
  APP_DATA_KEYS,
  BREADCRUMBS,
  NAV_TAB_NUMBERS,
  PAGE_NUMBERS,
  QUESTION_CODES,
  QUESTION_PATHS,
  READ_ONLY_ACCESS_PAGE_ACTION_BUTTONS
} from '../../constants/app-data.constants';
import { DASHBOARD_FILTER_NAMES } from '../../constants/grid-data-filter.constants';

import { MaatNcqGridFilterRequest } from '../../interfaces/maat-ncq-grid-filter-request.interface';
import { LoggedInUserRoleFlags } from '../../interfaces/logged-in-user-role-flags.interface';
import { LoggedInUserProfile } from '../../interfaces/logged-in-user-profile.interface';

import { PagePath } from '../../helpers/config-data-parser';
import { MaatNcqAccess } from '../../helpers/maat-ncq-access';
import { MaatAuditPlanAccess } from '../../helpers/maat-audit-plan-access';
import { MaatAuditPlanStatus } from '../../constants/maat-audit-plan-status.constants';
import { GridDataFilterHandler } from '../../helpers/grid-data-filter-handler';
import { GridDataClientHandler } from '../../helpers/grid-data-client-handler';

import { ServiceWorkerService } from '../../services/service-worker.service';
import { AppDataService } from '../../services/app-data.service';
import { ResponseParserService } from '../../services/response-parser.service';
import { FormConfigurationParserService } from '../../services/form-configuration-parser.service';
import { HtmlHelperService } from '../../services/html-helper.service';

import { BaseCardSetListComponent } from '../base-card-set-list-component/base-card-set-list.component';

@Component({
  selector: 'app-maat-ncq-list',
  templateUrl: './maat-ncq-list.component.html',
  styleUrls: ['./maat-ncq-list.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MaatNcqListComponent extends BaseCardSetListComponent implements OnInit, OnDestroy {

  private loggedInUserProfile: LoggedInUserProfile;
  private maatNcqFilterHandler: GridDataFilterHandler<MaatNcqGridFilterRequest>;

  private maatNcqGridDataClientHandler: GridDataClientHandler;

  activeDialog: string = '';
  maatAuditPlanId: string = '';
  maatJobId: string = '';
  maatJobItemId: string = '';
  maatAuditPlanStatus: MaatAuditPlanStatus;

  constructor(
    protected router: Router,
    protected serviceWorkerService: ServiceWorkerService,
    protected appDataService: AppDataService,
    protected spinnerService: SpinnerService,
    protected htmlHelperService: HtmlHelperService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected formNavigationService: FormNavigationService,
    private route: ActivatedRoute,
    private coreService: CoreService,
    private formConfigurationParserService: FormConfigurationParserService) {

    super(
      router,
      serviceWorkerService,
      appDataService,
      spinnerService,
      htmlHelperService,
      dialogService,
      responseParserService,
      formNavigationService);

    this.initialise(
      TEMPLATES.maatNcqList,
      () => `${API_ENDPOINTS.dashboard.getMaatNcqs}/${this.maatJobItemId}`,
      true);

    this.initialiseReturnNavigationState();

    this.maatNcqFilterHandler = new GridDataFilterHandler<MaatNcqGridFilterRequest>(
      this.responseParserService,
      this.appDataService,
      () => this.baseForm.model,
      {
        getLoggedInUserEmail: () => this.loggedInUserProfile?.emailAddress,
        getFilterName: () => DASHBOARD_FILTER_NAMES.maatNcqsByJobItem(this.maatJobItemId),
        searchQuestionPath: [...QUESTION_PATHS.maatNcqList.search],
        filterQuestionPath: [...QUESTION_PATHS.maatNcqList.filter],
        getFilterRequestFromModels: (searchQuestionModel: any, filterQuestionModel: any) => ({
          searchTerm: searchQuestionModel,
          ncqStatuses: filterQuestionModel?.ncqStatuses,
          category: filterQuestionModel?.category,
          headContractorPosition: filterQuestionModel?.headContractorPosition,
          businessPosition: filterQuestionModel?.businessPosition,
          tradeCode: filterQuestionModel?.tradeCode,
          responseRequiredByFrom: filterQuestionModel?.responseRequiredByFrom,
          responseRequiredByTo: filterQuestionModel?.responseRequiredByTo,
          navTabIndex: NAV_TAB_NUMBERS.maatNcqList.maatNcqs
        }),
        getDefaultFilterRequest: () => ({
          ncqStatuses: this.getMaatNcqAccess().getDefaultNcqStatuses(),
          navTabIndex: NAV_TAB_NUMBERS.maatNcqList.maatNcqs
        })
      });

    this.maatNcqGridDataClientHandler = new GridDataClientHandler(
      this.appDataService,
      () => this.formConfigurationParserService.findQuestionConfig(this.baseForm.fields, ...QUESTION_PATHS.maatNcqList.placeholder),
      () => this.loggedInUserProfile?.emailAddress,
      () => DASHBOARD_FILTER_NAMES.maatNcqsByJobItem(this.maatJobItemId));
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((paramMap: ParamMap) => {
      this.maatAuditPlanId = paramMap.get('maatAuditPlanId');
      this.maatJobId = paramMap.get('maatJobId');
      this.maatJobItemId = paramMap.get('maatJobItemId');
      if (this.maatJobItemId) {
        this.formServiceEndpoint = API_ENDPOINT_KEYS.getMaatJobItemConfigAndResponse;
      }
    });

    this.coreService.conditionFunctions['isReadOnly'] = () => true;

    this.overrideHtmlTitleSetByDspxFrameworkOnFormNavigation();

    this.waitForServiceWorker().then(waitResult => {

      this.loggedInUserProfile = waitResult.loggedInUserProfile;

      this.maatNcqFilterHandler.setInitialFilterRequest();
    });
  }

  ngOnDestroy(): void {

    this.maatNcqGridDataClientHandler.unsubscribe();

    this.completeDestroy();
  }

  cancel(event: any): void {
    this.navigate(['/', ROUTE_CONSTANTS.maatJobItem.listPrefix, this.maatAuditPlanId, this.maatJobId]);
  }

  formLoad(event: any): void {
    const parentId = this.maatJobItemId || '';
    const additionalData = event?.customConfigData?.[parentId] ?? {};
    const { maatAuditPlanHeading, maatJobHeading, maatJobItemHeading } = additionalData;
    this.appDataService.setBreadcrumbs([
      { label: BREADCRUMBS.pageHeadings.auditPlans, url: ROUTE_CONSTANTS.maatAuditPlan.list },
      { label: maatAuditPlanHeading, url: `${ROUTE_CONSTANTS.maatJob.listPrefix}/${this.maatAuditPlanId}` },
      { label: maatJobHeading, url: `${ROUTE_CONSTANTS.maatJobItem.listPrefix}/${this.maatAuditPlanId}/${this.maatJobId}` },
      { label: maatJobItemHeading }
    ]);

    this.setMaatAuditPlanStatus();
    this.configData = event.configData;
    // On the form load event the base form model is populated with the job item details, but the model remains undefined.
    // We set the model to the base form model to ensure that job item details are populated along with the grid data.
    this.model = this.baseForm.model;

    this.maatNcqGridDataClientHandler.copyConfigurationFromStoreIntoGrid()
      .then(() => this.maatNcqGridDataClientHandler.subscribe())
      .then(() => this.loadNcqsGridData(() => this.maatNcqFilterHandler.getInitialFilterRequest()));

    this.setAllTabsHeading(maatJobItemHeading);
    this.modelKeysToIgnore = [
      QUESTION_CODES.maatNcqList.maatNcqPlaceholder,
      QUESTION_CODES.maatNcqList.maatNcqSearch,
      QUESTION_CODES.maatNcqList.maatNcqFilter
    ]
    this.setPreviousModel({ isAsync: true });

    const pagePaths: PagePath[] = [
      [NAV_TAB_NUMBERS.maatNcqList.maatJobItem, PAGE_NUMBERS.maatNcqList.maatJobItem],
      [NAV_TAB_NUMBERS.maatNcqList.maatNcqs, PAGE_NUMBERS.maatNcqList.maatNcqs]
    ];
    this.ensurePageActionButtonsAreEnabledOnFormLoad(pagePaths, READ_ONLY_ACCESS_PAGE_ACTION_BUTTONS.maatNcqList);

    this.restoreReturnActiveTab();
  }

  filterData(event: any): void {
    this.loadNcqsGridData(() => this.maatNcqFilterHandler.saveFilterRequest(event.action));
  }

  private loadNcqsGridData(getfilterRequest = this.maatNcqFilterHandler.getFilterRequest): void {
    const onGridLoaded = () => this.maatNcqGridDataClientHandler.copyConfigurationFromStoreIntoGrid();
    this.loadGridData(getfilterRequest(), onGridLoaded, NAV_TAB_NUMBERS.maatNcqList.maatNcqs);
  }

  private setAllTabsHeading(heading: string): void {
    const pageConfigs = [
      this.formConfigurationParserService.findPageConfig(this.baseForm.fields, NAV_TAB_NUMBERS.maatNcqList.maatJobItem, PAGE_NUMBERS.maatNcqList.maatJobItem),
      this.formConfigurationParserService.findPageConfig(this.baseForm.fields, NAV_TAB_NUMBERS.maatNcqList.maatNcqs, PAGE_NUMBERS.maatNcqList.maatNcqs)
    ]
      .filter(pageConfig => !!pageConfig); // pages might be missing depending on the logged-in user role

    pageConfigs.forEach(pageConfig => {
      pageConfig.props.heading = heading;
    });
  }

  private getMaatAuditPlanAccess(): MaatAuditPlanAccess {
    return new MaatAuditPlanAccess(
      this.appDataService.getAppData<LoggedInUserRoleFlags>(APP_DATA_KEYS.loggedInUserRoleFlags),
      this.maatAuditPlanStatus);
  }

  private getMaatNcqAccess(): MaatNcqAccess {
    return new MaatNcqAccess(
      this.appDataService.getAppData<LoggedInUserRoleFlags>(APP_DATA_KEYS.loggedInUserRoleFlags));
  }

  private setMaatAuditPlanStatus(): void {
    if (this.maatAuditPlanId) {
      const endpoint = `${API_ENDPOINTS.maatAuditPlan.getConfigAndResponse}/${TEMPLATES.maatJobList}/0/${this.maatAuditPlanId}`;
      this.appDataService.getData<any>(endpoint)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: entityData => {
            const planStatus = this.getQuestionValue(entityData?.response, ...QUESTION_PATHS.maatJobList.planStatus);
            if (planStatus != null) {
              this.maatAuditPlanStatus = planStatus;
            }
          }
        });
    }
  }

  actionButton_maatNcqPlaceholder_datagrid_custom_customAdd_Clicked(event: any) {
    if (!this.getMaatAuditPlanAccess().canEdit()) {
      this.sharedSimplePopup.showNoPermissionPopup();
      return;
    }

    this.navigate(
      ['/', ROUTE_CONSTANTS.maatNcq.prefix, this.maatAuditPlanId, this.maatJobId, this.maatJobItemId],
      false,
      {
        state: {
          returnRoute: ['/', ROUTE_CONSTANTS.maatNcq.listPrefix, this.maatAuditPlanId, this.maatJobId, this.maatJobItemId],
          activeTab: NAV_TAB_NUMBERS.maatNcqList.maatNcqs
        }
      }
    );
  }

  actionButton_maatNcqPlaceholder_datagrid_details_Clicked(event: any): void {
    const maatNcqId = event.event.data.id;
    this.navigate(
      ['/', ROUTE_CONSTANTS.maatNcq.prefix, this.maatAuditPlanId, this.maatJobId, this.maatJobItemId, maatNcqId],
      false,
      {
        state: {
          returnRoute: ['/', ROUTE_CONSTANTS.maatNcq.listPrefix, this.maatAuditPlanId, this.maatJobId, this.maatJobItemId],
          activeTab: NAV_TAB_NUMBERS.maatNcqList.maatNcqs
        }
      }
    );
  }
}
